import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Briefcase, ClipboardList, Eye, EyeOff, RefreshCw, Save,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { CRE_HANDOVER_CONFIG_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

const TYPE_LABEL = {
    text: "Text", textarea: "Notes", number: "Number", money: "Amount",
    boolean: "Yes / No", date: "Date", choice: "Pick one", multi: "Pick many",
};

/**
 * Whether the selected project runs a Sales-to-CRE handover step, and which of
 * its fields it asks for.
 *
 * Two switches, not one, because "we do not run a handover" and "we run one but
 * do not ask about white labelling" are different decisions. The master switch
 * shows or hides the whole tab on every lead in the project; the per-field
 * switches trim what the tab asks for.
 *
 * The scopes differ, and the screen says so rather than leaving it to be
 * discovered: the master switch is per project (a process decision that varies
 * between teams), the field switches are account-wide (a data-model decision
 * that does not).
 *
 * Switching a field off stops it being collected; it does not erase or hide
 * what has already been recorded. The count next to each field is how many
 * leads would keep showing it, so the consequence is visible before the switch
 * flips.
 */
export default function CREHandoverConfig() {
    const { fetch, update } = useApiMethods();

    const [groups, setGroups]       = useState([]);
    const [enabled, setEnabled]     = useState({});
    const [isEnabled, setIsEnabled] = useState(false);
    const [scope, setScope]         = useState({ name: "", own: false });
    const [leadCount, setLeads]     = useState(0);
    const [canEdit, setCanEdit]     = useState(false);
    const [loading, setLoading]     = useState(false);
    const [saving, setSaving]       = useState(false);
    const [dirty, setDirty]         = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        fetch(CRE_HANDOVER_CONFIG_URL)
            .then(res => {
                if (res?.status === "success") {
                    setGroups(res.groups || []);
                    setIsEnabled(!!res.is_enabled);
                    setScope({
                        name: res.project_name || "",
                        own: !!res.has_project_setting,
                    });
                    setLeads(res.lead_count || 0);
                    setCanEdit(!!res.can_edit);
                    const map = {};
                    (res.groups || []).forEach(g =>
                        (g.fields || []).forEach(f => { map[f.key] = f.is_enabled; }));
                    setEnabled(map);
                    setDirty(false);
                } else {
                    ToastMsg("error", res?.message || "Failed to load the handover settings");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load the handover settings"))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = (key) => {
        if (!canEdit) return;
        setEnabled(e => ({ ...e, [key]: !e[key] }));
        setDirty(true);
    };

    const toggleGroup = (group, on) => {
        if (!canEdit) return;
        setEnabled(e => {
            const next = { ...e };
            (group.fields || []).forEach(f => { next[f.key] = on; });
            return next;
        });
        setDirty(true);
    };

    const save = () => {
        setSaving(true);
        update(CRE_HANDOVER_CONFIG_URL, { is_enabled: isEnabled, fields: enabled })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", "Handover settings saved");
                    setDirty(false);
                } else ToastMsg("error", res?.message || "Could not save");
            })
            .catch(() => ToastMsg("error", "Could not save"))
            .finally(() => setSaving(false));
    };

    const onCount = useMemo(
        () => Object.values(enabled).filter(Boolean).length, [enabled]);
    const total = Object.keys(enabled).length;

    return (
        <ContainerCard>
            <div className="p-4 max-w-3xl">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2">
                        <ClipboardList className="h-5 w-5 text-indigo-500" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                CRE Handover
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                What sales records for the implementation team when a deal is won.
                                {" "}{onCount} of {total} fields on ·{" "}
                                {leadCount} lead{leadCount === 1 ? "" : "s"} with a handover
                            </p>
                            {scope.name && (
                                <p className="mt-0.5 text-[11px] text-gray-400 flex items-center gap-1">
                                    <Briefcase className="h-3 w-3" />
                                    Project <span className="font-medium text-gray-600 dark:text-gray-300">
                                        {scope.name}
                                    </span>
                                    {" — switch the project at the top of the screen to configure another."}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={load} title="Reload"
                            className="h-8 w-8 rounded-md border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center">
                            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
                        </button>
                        {canEdit && (
                            <button onClick={save} disabled={!dirty || saving}
                                className="h-8 px-3 rounded-md text-xs font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white flex items-center gap-1.5">
                                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
                            </button>
                        )}
                    </div>
                </div>

                {!canEdit && (
                    <p className="mb-4 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                        Only a super admin can change these. You are seeing the current settings.
                    </p>
                )}

                {/* ── the master switch ── */}
                <div className={cn(
                    "rounded-xl border p-3 mb-4 transition",
                    isEnabled
                        ? "border-indigo-200 bg-indigo-50/60 dark:border-indigo-800 dark:bg-indigo-900/20"
                        : "border-gray-200 dark:border-gray-700"
                )}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-800 dark:text-gray-100">
                                Collect handover details
                                {scope.name && (
                                    <span className="ml-1 font-normal text-gray-400">
                                        for {scope.name}
                                    </span>
                                )}
                            </p>
                            <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                                Adds a <span className="font-medium">CRE Handover</span> tab to
                                every lead in this project, for recording what was sold and
                                promised. Leave it off if the team does not hand deals over to
                                a separate implementation group.
                            </p>
                            {scope.name && !scope.own && (
                                <p className="mt-1 text-[10px] text-gray-400">
                                    This project has no setting of its own yet, so it is
                                    following the account default. Saving here gives it one,
                                    and other projects stay as they are.
                                </p>
                            )}
                        </div>
                        <button
                            onClick={() => { if (canEdit) { setIsEnabled(v => !v); setDirty(true); } }}
                            disabled={!canEdit}
                            role="switch" aria-checked={isEnabled}
                            className={cn(
                                "relative h-6 w-11 rounded-full flex-shrink-0 transition",
                                isEnabled ? "bg-indigo-600" : "bg-gray-300 dark:bg-gray-600",
                                canEdit ? "cursor-pointer" : "cursor-default opacity-70"
                            )}>
                            <span className={cn(
                                "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all",
                                isEnabled ? "left-[22px]" : "left-0.5"
                            )} />
                        </button>
                    </div>

                    {!isEnabled && leadCount > 0 && (
                        <p className="mt-2 text-[10px] text-amber-700 dark:text-amber-300">
                            {leadCount} lead{leadCount === 1 ? " has" : "s have"} a handover
                            recorded already. Turning this off keeps that readable on those
                            leads but stops anything new being recorded.
                        </p>
                    )}
                </div>

                {/* ── per-field switches ── */}
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                    Which fields the tab asks for
                    <span className="text-gray-400"> — account-wide, every project</span>
                    {!isEnabled && (
                        <span className="text-gray-400">, and takes effect once the switch above is on</span>
                    )}
                </p>

                <div className={cn("space-y-4", !isEnabled && "opacity-60")}>
                    {groups.map(group => {
                        const fields = group.fields || [];
                        const allOn = fields.every(f => enabled[f.key]);
                        return (
                            <div key={group.group}
                                className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                <div className="px-3 py-1.5 flex items-center gap-2 bg-gray-50 dark:bg-gray-700/40 border-b border-gray-100 dark:border-gray-700">
                                    <span className="min-w-0 flex-1">
                                        <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            {group.group}
                                        </span>
                                        {group.help_text && (
                                            <span className="block text-[10px] text-gray-400 normal-case tracking-normal mt-0.5">
                                                {group.help_text}
                                            </span>
                                        )}
                                    </span>
                                    {canEdit && (
                                        <button onClick={() => toggleGroup(group, !allOn)}
                                            className="text-[10px] text-indigo-600 dark:text-indigo-400 hover:underline flex-shrink-0">
                                            {allOn ? "Turn all off" : "Turn all on"}
                                        </button>
                                    )}
                                </div>
                                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {fields.map(f => {
                                        const on = !!enabled[f.key];
                                        return (
                                            <li key={f.key} className="flex items-center gap-3 px-3 py-2">
                                                <button
                                                    onClick={() => toggle(f.key)}
                                                    disabled={!canEdit}
                                                    title={on ? "Collected" : "Not collected"}
                                                    className={cn(
                                                        "h-6 w-6 rounded-md border flex items-center justify-center flex-shrink-0 transition",
                                                        on
                                                            ? "border-indigo-200 bg-indigo-50 text-indigo-600 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                                                            : "border-gray-200 text-gray-400 dark:border-gray-600",
                                                        canEdit ? "hover:bg-gray-100 dark:hover:bg-gray-700" : "cursor-default opacity-70"
                                                    )}>
                                                    {on ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                                                </button>

                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={cn(
                                                            "text-xs font-medium",
                                                            on ? "text-gray-800 dark:text-gray-200"
                                                               : "text-gray-400 dark:text-gray-500 line-through"
                                                        )}>
                                                            {f.label}
                                                        </span>
                                                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                            {TYPE_LABEL[f.type] || f.type}
                                                        </span>
                                                        {f.unit && (
                                                            <span className="text-[9px] text-gray-400">in {f.unit}</span>
                                                        )}
                                                        {f.is_required && (
                                                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                                                title="A deal cannot be marked Closed Won until this is answered. Switching it off removes that check.">
                                                                blocks Closed Won
                                                            </span>
                                                        )}
                                                    </div>
                                                    {f.help_text && (
                                                        <p className="mt-0.5 text-[10px] text-gray-400 italic">
                                                            {f.help_text}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Says what switching off would NOT do. */}
                                                {f.answered_count > 0 && (
                                                    <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0"
                                                        title="These leads keep showing this field even when it is switched off">
                                                        {f.answered_count} answered
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </div>
        </ContainerCard>
    );
}
